import React from 'react'
import {
  PreviewCallout,
  PREVIEW_PAGE_ENUM,
} from '@public/components/PreviewCallout'
import { Error } from 'components/Error'
import { LOADER_SIZE } from 'components/Loader'
import { PureSSRIndicator } from 'components/PureSSRIndicator'
import { Seo } from 'components/Seo'
import { WidgetsWrapper } from 'components/Widgets'
import { PAGE_MIN_HEIGHT, WrappedLoader } from 'components/WrappedLoader'
import { ROUTE } from 'constants/routes'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import {
  GA4_AREAS,
  GA4_ITEM_LIST_NAME,
  GA4_PAGE_AREAS,
} from 'providers/googleAnalytics'
import { SPACE, WIDGET_MAX_WIDTH } from 'Theme'
import { type CmsPageType, type CmsWidget } from 'types/graphql-generated'
import { getSeoPropsForCmsPage } from '../getSeoPropsForCmsPage'

export interface ShowroomPageViewProps {
  slug: string
  data: CmsPageType
  isLoading: boolean
  widgets: CmsWidget[]
  isPreview?: boolean
  isSearchFilterParamsMode?: boolean
  pageTitle?: string
  fallbackMetaTitle?: string
  fallbackDescription?: string
  canonicalUrl?: string
}

const GENERAL_SLUGS = {
  SHOWROOM: 'showroom',
  PRODUCTS: 'products',
}

const getListNameFromSlug = (slug: string) => {
  if (slug === GENERAL_SLUGS.PRODUCTS) {
    return `${GA4_ITEM_LIST_NAME.COLLECTION} / ${GA4_ITEM_LIST_NAME.SHOWROOM} / ${slug}`
  }

  if (slug === GENERAL_SLUGS.SHOWROOM) {
    return slug
  }
  return `${GA4_ITEM_LIST_NAME.COLLECTION} / ${slug}`
}

const ShowroomPageViewContent: React.FC<React.PWC<ShowroomPageViewProps>> = ({
  slug,
  data,
  isLoading,
  widgets,
  isPreview,
  isSearchFilterParamsMode,
  pageTitle,
  fallbackMetaTitle,
  fallbackDescription,
  canonicalUrl = `/${slug}`,
}) => {
  if (isLoading && !data) {
    return (
      <WrappedLoader
        loaderSize={LOADER_SIZE.XXXL}
        minHeight={PAGE_MIN_HEIGHT}
      />
    )
  }

  if (data == null) {
    return <Error />
  }

  const seoProps = {
    ...getSeoPropsForCmsPage(data, {
      fallbackMetaTitle,
      fallbackDescription,
    }),
    ...(pageTitle && { title: pageTitle }),
    canonicalUrl,
  }

  return (
    <>
      <Seo {...seoProps} />
      {isPreview && (
        <PreviewCallout
          previewOption={PREVIEW_PAGE_ENUM.SHOWROOM_PAGE_PREVIEW}
          backButtonHref={ROUTE.ADMIN_SHOWROOM_PAGE({ id: slug })}
        />
      )}
      <WidgetsWrapper
        widgets={widgets}
        slug={slug}
        mb={{ TABLET: SPACE.PX_40, MOBILE: SPACE.PX_5 }}
        maxWidgetWidth={WIDGET_MAX_WIDTH.ARTICLE}
        isSearchFilterParamsMode={isSearchFilterParamsMode}
      />
      <PureSSRIndicator />
    </>
  )
}

export const ShowroomPageView: React.FC<React.PWC<ShowroomPageViewProps>> = ({
  slug,
  ...rest
}) =>
  withGA4Provider({
    area: GA4_AREAS.SHOWROOM,
    page_area: GA4_PAGE_AREAS.SCP,
    item_list_name: `${
      GA4_ITEM_LIST_NAME.CATEGORY_PAGE
    } / ${getListNameFromSlug(slug)}`,
  })(ShowroomPageViewContent)({
    slug,
    ...rest,
  })
